import type { CompactSpec } from "../compact"
import { expandSpec } from "../compact"

/** Vertical pipeline with rich descriptions on every node */
const compact: CompactSpec = {
  id: "vertical-pipeline",
  name: "VidMax: Full Production Pipeline",
  desc: "End-to-end AI video production flow with detailed step descriptions",
  layout: "TB",
  preferences: {
    nodeWidth: 260,
    rankSpacing: 120,
    nodeGap: 30,
    showLayerBands: true,
    edgeStyle: "smoothstep",
    edgeColor: "#4a6a8a",
    animateEdges: true,
  },
  layers: [
    { id: "input", name: "Input", color: "#818cf8" },
    { id: "script", name: "Scripting", color: "#f472b6" },
    { id: "media", name: "Media Generation", color: "#fb923c" },
    { id: "post", name: "Post-Production", color: "#34d399" },
    { id: "publish", name: "Distribution", color: "#60a5fa" },
  ],
  nodes: [
    {
      id: "topic",
      label: "Topic Research",
      icon: "🔍",
      layer: "input",
      rank: 0,
      desc: "Analyzes trending topics across YouTube, TikTok, and Reddit using Perplexity + YouTube Data API. Scores each topic by search volume, competition gap, and audience intent. Outputs a ranked topic brief with target keywords, competitor gaps, and suggested angles.",
    },
    {
      id: "outline",
      label: "Content Strategy",
      icon: "🧠",
      layer: "input",
      rank: 0,
      desc: "Takes the topic brief and generates a content strategy document. Defines the hook (first 3 seconds), narrative arc, key retention points, and CTA placement. Uses Claude Opus for deep reasoning about what makes this topic compelling and what angle competitors missed.",
    },
    {
      id: "script",
      label: "Script Generation",
      icon: "✍️",
      layer: "script",
      rank: 1,
      desc: "Claude Sonnet writes the full script with timestamps, scene directions, and B-roll cues. Each sentence is optimized for spoken cadence — short punchy lines, strategic pauses, and pattern interrupts every 30 seconds to maintain retention. Outputs script.md + scene-prompts.json.",
    },
    {
      id: "voice",
      label: "Voice Synthesis",
      icon: "🎙️",
      layer: "media",
      rank: 2,
      desc: "ElevenLabs Turbo v2.5 generates the voiceover from the script. Uses a custom cloned voice trained on 30 minutes of reference audio. Applies dynamic pacing — faster for excitement, slower for emphasis. Outputs a WAV file with word-level timestamps for precise lip sync.",
    },
    {
      id: "visuals",
      label: "Visual Generation",
      icon: "🎬",
      layer: "media",
      rank: 2,
      desc: "Seedance 1.0 generates talking-head video clips from the audio + reference image. Each clip is 5-10 seconds matching the scene prompts. For B-roll scenes, Veo 3 generates cinematic footage from text descriptions. All clips rendered at 1080p with consistent color grading.",
    },
    {
      id: "music",
      label: "Background Music",
      icon: "🎵",
      layer: "media",
      rank: 2,
      desc: "Udio generates a custom background track matching the video mood and pacing. Takes genre, tempo, and energy curve as inputs. Produces a stems-separated mix (melody, bass, drums, ambient) so the editor can duck music under speech without artifacts.",
    },
    {
      id: "edit",
      label: "Auto-Edit & Assembly",
      icon: "✂️",
      layer: "post",
      rank: 3,
      desc: "NanoBanana assembles the final video from all generated assets. Sequences talking-head clips with B-roll based on scene prompts. Applies jump cuts at silence gaps, adds zoom effects on key moments, overlays captions with word-by-word highlighting, and mixes the music track at -12dB under speech.",
    },
    {
      id: "thumbnail",
      label: "Thumbnail Generation",
      icon: "🖼️",
      layer: "post",
      rank: 3,
      desc: "Generates 3 thumbnail variants using Flux Pro. Each variant tests a different emotional hook — curiosity, shock, or authority. Applies the proven thumbnail formula: large face, 3-4 word text overlay, high contrast background, and a visual element that creates an information gap.",
    },
    {
      id: "qa",
      label: "Quality Assurance",
      icon: "🔍",
      layer: "post",
      rank: 4,
      desc: "Automated QA pipeline checks: audio levels normalized to -14 LUFS, no jump cuts under 0.5s, captions match audio within 100ms, thumbnail text is readable at 120px width, video resolution matches platform specs, and content passes brand safety filters. Flags issues for human review.",
    },
    {
      id: "youtube",
      label: "YouTube Upload",
      icon: "📺",
      layer: "publish",
      rank: 5,
      desc: "Uploads to YouTube via API with optimized metadata. Title A/B tested across 3 variants, description stuffed with long-tail keywords, chapters auto-generated from script timestamps, end screen pointing to next video in the series, and cards placed at each pattern interrupt moment.",
    },
    {
      id: "shorts",
      label: "Shorts / Reels",
      icon: "📱",
      layer: "publish",
      rank: 5,
      desc: "Auto-crops the best 60-second segment for vertical distribution. Uses retention prediction to pick the highest-engagement clip. Re-renders at 9:16 with larger captions, faster pacing, and a hook-first reorder. Publishes simultaneously to YouTube Shorts, TikTok, and Instagram Reels.",
    },
  ],
  edges: [
    ["topic", "outline"],
    ["outline", "script"],
    ["script", "voice"],
    ["script", "visuals"],
    ["script", "music"],
    ["voice", "edit"],
    ["visuals", "edit"],
    ["music", "edit"],
    ["edit", "thumbnail"],
    ["edit", "qa"],
    ["thumbnail", "qa"],
    ["qa", "youtube"],
    ["qa", "shorts"],
  ],
}

export const verticalPipelineDiagram = expandSpec(compact)
